import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  MuiThemeProvider,
  createMuiTheme,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getBacktestingConfigById, postBacktestingConfig, updateBacktestingConfig, deleteBacktestingConfig } from "../../../../api/backtestingConfig";
import { getAllCoins } from "../../../../api/strategy";
import EditBacktestingConfigPageInfo from "./EditBacktestingConfigPageInfo";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#cc02d6",
    },
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyBacktestingConfig() {
  return {
    fullName: "",
    isMain: false,
    interval: 120,
    emaPeriod: 3,
    filterTop: 0,
    filterBottom: -2.5,
    percentageComission: 0.045,
    longs: true,
    shorts: true,
    source: "close",
    active: true,
  };
}

export default function EditBacktestingConfigPage() {
  const [backtestingConfig, setBacktestingConfig] = useState(getEmptyBacktestingConfig());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [allCoins, setAllCoins] = useState([]);
  const [separatedCoins, setSeparatedCoins] = useState({usdt: [], usdc: []})
  const backtestingConfigId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    fetchCoins();
  }, [])

  useEffect(() => {
    if (!backtestingConfigId) { disableLoadingData(); return; }
    getBacktestingConfigById(backtestingConfigId)
      .then((res) => {
        if (res.status === 200) {
          setBacktestingConfig(res.data);
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({ error: error, customMessage: "Could not get backtestingConfig." });
        history.push("/backtesting-config");
      });
  }, [backtestingConfigId, disableLoadingData, history]);

  async function fetchCoins() {
    const res = await getAllCoins("Bingx");
    if (res) {
      setAllCoins([...res.data]);
      
      const { usdt, usdc } = res.data.reduce(
        (acc, symbol) => {
          if (symbol.endsWith("USDT")) acc.usdt.push(symbol);
          else acc.usdc.push(symbol);
          return acc;
        },
        { usdt: [], usdc: [] }
      );
      setSeparatedCoins({usdt, usdc})
    }
  }

  function saveBacktestingConfig() {
    let saveBacktestingConfig = backtestingConfig;
    if (!backtestingConfigId) {
      postBacktestingConfig(saveBacktestingConfig)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "BacktestingConfig successfully created.",
            });
            history.push("/backtesting-config");
          }
        })
        .catch((error) => {
          alertError({ error: error, customMessage: "Could not save backtestingConfig." });
        });
    } else {
      updateBacktestingConfig(backtestingConfigId, saveBacktestingConfig)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/backtesting-config");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element) => (event) => {
    if (event.target.value === " ") return;
    setBacktestingConfig({ ...backtestingConfig, [element]: event.target.value });
  };

  const handleFormChange = (element, value) => {
    setBacktestingConfig({...backtestingConfig, [element]: value})
  }

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <MuiThemeProvider theme={theme}>
        <Card>
          <CardHeader title="Edit backtestingConfig"></CardHeader>
          <CardBody>
            <EditBacktestingConfigPageInfo 
              backtestingConfig={backtestingConfig}
              setBacktestingConfig={setBacktestingConfig}
              allCoins={allCoins}
              separatedCoins={separatedCoins}
              handleChange={handleChange}
              handleFormChange={handleFormChange}
              readonly={false}
            />
          </CardBody>
        </Card>
        <Button
          onClick={() => history.push("/backtesting-config")}
          variant="outlined"
          style={{ marginRight: "20px" }}
        >
          Back
        </Button>
        <Button
          onClick={() => saveBacktestingConfig()}
          variant="outlined"
          color="primary"
          style={{ marginRight: "20px" }}
        >
          Save backtestingConfig
        </Button>
        {backtestingConfigId && !backtestingConfig?.isMain && (
          <>
            <MuiThemeProvider theme={theme}>
              <Button
                onClick={() => setOpenConfirmDialog(true)}
                variant="outlined"
                color="secondary"
              >
                Delete backtestingConfig
              </Button>
            </MuiThemeProvider>

            <ConfirmDialog
              title={"Are you sure you want to delete this backtestingConfig?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteBacktestingConfig(backtestingConfigId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "BacktestingConfig deleted successfully",
                      });
                      history.push("/backtesting-config");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete backtestingConfig.",
                    });
                  });
              }}
            />
          </>
        )}
      </MuiThemeProvider>
    );
}
